import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Heart, Lock, MessageCircle, Sparkles, Award, MapPin, GraduationCap, Briefcase, MoreVertical, Star, EyeOff, Ban, Flag, Phone as PhoneIcon, Handshake, Check, X, Pencil, Linkedin, Instagram, Facebook, Twitter, Youtube, Globe, Scale } from "lucide-react";
import { TrustBadges } from "@/components/umaang/TrustBadges";
import { celebrateMatch } from "@/lib/celebrate";

const SOCIAL_META = {
  linkedin:  { Icon: Linkedin,  label: "LinkedIn",     color: "#0A66C2" },
  instagram: { Icon: Instagram, label: "Instagram",    color: "#E1306C" },
  facebook:  { Icon: Facebook,  label: "Facebook",     color: "#1877F2" },
  twitter:   { Icon: Twitter,   label: "X (Twitter)",  color: "#0F172A" },
  youtube:   { Icon: Youtube,   label: "YouTube",      color: "#FF0000" },
  website:   { Icon: Globe,     label: "Website",      color: "#7C3AED" },
};

const EMOJIS = ["❤️", "😊", "🌹", "💐", "🙏", "✨"];

function Field({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="border-b py-3" style={{ borderColor: "var(--umg-border)" }}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--umg-text-2)]">{label}</div>
      <div className="text-sm mt-1">{value}</div>
    </div>
  );
}

export default function ProfileDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [showEmojis, setShowEmojis] = useState(false);
  const [horo, setHoro] = useState(null);
  const [connectStatus, setConnectStatus] = useState({ sent: null, received: null });
  const [connectOpen, setConnectOpen] = useState(false);
  const [connectMsg, setConnectMsg] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [icebreakerOpen, setIcebreakerOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [me, setMe] = useState(null); // full "my profile" for compare
  const [icebreakers, setIcebreakers] = useState(null);
  const [icebreakerLoading, setIcebreakerLoading] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [contact, setContact] = useState(null);

  const loadStatus = async () => {
    try {
      const r = await api.get(`/connect/status/${id}`);
      setConnectStatus(r.data);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("connect-status load failed", e);
    }
  };

  useEffect(() => {
    api.get(`/profiles/${id}`).then(({ data }) => setP(data)).catch((e) => toast.error(formatError(e)));
    api.get(`/horoscope/${id}`).then(({ data }) => setHoro(data)).catch(() => {});
    loadStatus();
    // Fetch signed-in user's own full profile so the Compare dialog can render both columns
    api.get("/me").then(({ data }) => setMe(data)).catch(() => {});
    // eslint-disable-next-line
  }, [id]);

  if (!p) return <div className="max-w-6xl mx-auto p-10 text-[color:var(--umg-text-2)]">Loading…</div>;
  const isOwner = user?.id && p?.id && String(user.id) === String(p.id);

  const doLike = async () => {
    try { await api.post(`/profiles/${p.id}/like`); toast.success(`You liked ${p.name}`); }
    catch (e) { toast.error(formatError(e)); }
  };
  const doEmoji = async (e) => {
    try { await api.post(`/profiles/${p.id}/emoji`, { emoji: e }); toast.success(`Sent ${e}`); setShowEmojis(false); }
    catch (err) { toast.error(formatError(err)); }
  };
  const doConnect = async () => {
    try {
      const r = await api.post(`/connect/${p.id}`, { message: connectMsg });
      setConnectOpen(false); setConnectMsg("");
      toast.success(r.data.status === "pending" ? "Request sent" : `Status: ${r.data.status}`);
      loadStatus();
    } catch (e) { toast.error(formatError(e)); }
  };
  const doShortlist = async () => {
    try { await api.post(`/shortlist/${p.id}`); toast.success("Added to shortlist"); }
    catch (e) { toast.error(formatError(e)); }
  };
  const doHide = async () => {
    try { await api.post(`/hide/${p.id}`); toast.success("Won't show again"); nav("/home"); }
    catch (e) { toast.error(formatError(e)); }
  };
  const doBlock = async () => {
    if (!window.confirm(`Block ${p.name}? They can't contact you and you won't see them.`)) return;
    try { await api.post(`/block/${p.id}`); toast.success("Blocked"); nav("/home"); }
    catch (e) { toast.error(formatError(e)); }
  };
  const submitReport = async () => {
    try {
      await api.post("/report", { profile_id: p.id, target_type: "profile", reason: reportReason });
      toast.success("Reported. Our team will review.");
      setReportOpen(false); setReportReason("");
    } catch (e) { toast.error(formatError(e)); }
  };
  const viewContact = async () => {
    try {
      const r = await api.post(`/contact/view/${p.id}`);
      setContact(r.data);
      toast.success("Contact revealed");
    } catch (e) { toast.error(formatError(e)); }
  };
  const respond = async (action) => {
    if (!connectStatus.received?.id) return;
    try {
      await api.post(`/connect/${connectStatus.received.id}/${action}`);
      if (action === "accept") {
        celebrateMatch();
        toast.success("🎉 Rishta jud gaya!");
      } else {
        toast.success("Rejected");
      }
      loadStatus();
    }
    catch (e) { toast.error(formatError(e)); }
  };

  const sent = connectStatus.sent;
  const received = connectStatus.received;

  return (
    <div className="max-w-6xl mx-auto px-6 py-10 pb-32">
      <div className="grid md:grid-cols-5 gap-8">
        {/* Photos */}
        <div className="md:col-span-2">
          <div className="border overflow-hidden" style={{ borderRadius: 8, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }}>
            {p.photos_visible?.[0] ? (
              <img src={p.photos_visible[0]} alt={p.name} className="w-full aspect-[4/5] object-cover" />
            ) : (
              <div className="w-full aspect-[4/5] bg-[color:var(--umg-secondary)] grid place-items-center">
                <Heart className="w-16 h-16 text-[color:var(--umg-border)]" />
              </div>
            )}
          </div>
          {p.photos_locked_count > 0 && (
            <div className="mt-4">
              <div className="grid grid-cols-3 gap-2">
                {Array.from({ length: Math.min(p.photos_locked_count, 9) }).map((_, i) => (
                  // eslint-disable-next-line react/no-array-index-key -- placeholders have no data; count-based render only
                  <div key={`lock-${i}`} className="locked-photo aspect-square border" style={{ borderRadius: 6, borderColor: "var(--umg-border)", background: "var(--umg-secondary)" }}>
                    <div className="absolute inset-0 grid place-items-center z-10">
                      <Lock className="w-5 h-5 text-[color:var(--umg-text-2)]" />
                    </div>
                  </div>
                ))}
              </div>
              <button data-testid="unlock-photos-btn" onClick={() => nav("/premium")} className="mt-3 w-full text-xs text-[#C85A53] hover:underline">
                {p.photos_locked_count} {t("photos_locked")} — {t("unlock_all")}
              </button>
            </div>
          )}
        </div>

        {/* Detail */}
        <div className="md:col-span-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="uppercase tracking-[0.28em] text-xs bg-gradient-to-r from-[#C85A53] via-[#DB2777] to-[#7C3AED] bg-clip-text text-transparent font-semibold">
                {(p.compatibility?.overall ?? p.match_percent)}% Compatibility
              </div>
              <h1 className="font-display text-4xl md:text-5xl mt-2 tracking-tight">{p.name}</h1>
              {p.member_id && (
                <div className="mt-1 inline-flex items-center gap-1.5 text-xs">
                  <span className="text-[10px] uppercase tracking-widest text-[color:var(--umg-text-2)]">Member ID</span>
                  <code className="text-[#7C3AED] font-semibold">{p.member_id}</code>
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-3 text-sm text-[color:var(--umg-text-2)]">
                {p.age && <span className="flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> {p.age} yrs</span>}
                {p.city && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {p.city}{p.state ? `, ${p.state}` : ""}</span>}
                {p.profession && <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> {p.profession}</span>}
                {p.education && <span className="flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5" /> {p.education}</span>}
              </div>
              {p.referred_by && (
                <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 border rounded-full text-xs" style={{ background: "var(--umg-secondary)", borderColor: "var(--umg-border)" }}>
                  <Award className="w-3.5 h-3.5 text-[#C85A53]" />
                  <span className="uppercase tracking-widest text-[10px] text-[color:var(--umg-text-2)]">{t("referred_by")}</span>
                  <span className="font-semibold">{p.referred_by.name}</span>
                  <span className="text-[color:var(--umg-text-2)]">· {p.referred_by.code}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0 flex-wrap">
              <TrustBadges profile={p} />
              {isOwner ? (
                <Link to="/profile/edit" data-testid="edit-profile-btn">
                  <Button className="btn-pill bg-gradient-to-r from-[#C85A53] to-[#A94842] hover:opacity-90 text-white h-9 px-4 shadow-md shadow-[#C85A53]/30">
                    <Pencil className="w-4 h-4 mr-1.5" /> Edit profile
                  </Button>
                </Link>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="more-menu-btn" className="p-2 rounded-full hover:bg-[color:var(--umg-secondary)] transition">
                      <MoreVertical className="w-5 h-5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem data-testid="menu-shortlist" onClick={doShortlist}>
                      <Star className="w-4 h-4 mr-2" /> Add to shortlist
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="menu-hide" onClick={doHide}>
                      <EyeOff className="w-4 h-4 mr-2" /> Don't show again
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid="menu-block" onClick={doBlock} className="text-[#C85A53]">
                      <Ban className="w-4 h-4 mr-2" /> Block profile
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="menu-report" onClick={() => setReportOpen(true)} className="text-[#C85A53]">
                      <Flag className="w-4 h-4 mr-2" /> Report profile / photo
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>

          {/* Incoming request banner */}
          {!isOwner && received?.status === "pending" && (
            <div data-testid="incoming-request-banner" className="mt-5 p-4 border flex items-center justify-between gap-4" style={{ borderRadius: 8, borderColor: "#C85A53", background: "var(--umg-surface)" }}>
              <div>
                <div className="text-sm font-semibold">{p.name} sent you a connection request</div>
                <div className="text-xs text-[color:var(--umg-text-2)] mt-0.5">Respond to start chatting.</div>
              </div>
              <div className="flex gap-2">
                <Button data-testid="detail-accept" size="sm" onClick={() => respond("accept")} className="btn-pill bg-[#4F7942] hover:bg-[#3d5f34] text-white h-9">
                  <Check className="w-4 h-4 mr-1" /> Accept
                </Button>
                <Button data-testid="detail-reject" size="sm" variant="outline" onClick={() => respond("reject")} className="btn-pill h-9">
                  <X className="w-4 h-4 mr-1" /> Decline
                </Button>
              </div>
            </div>
          )}

          {/* Actions */}
          {!isOwner && (
          <div className="mt-6 flex flex-wrap gap-2 relative">
            <Button data-testid="like-btn" onClick={doLike} className="btn-pill bg-[#C85A53] hover:bg-[#A94842] text-white">
              <Heart className="w-4 h-4 mr-1.5" fill="white" /> {t("like")}
            </Button>
            <Button data-testid="emoji-btn" onClick={() => setShowEmojis((s) => !s)} variant="outline" className="btn-pill">
              😊 Emoji
            </Button>
            <Link to={`/messages/${p.id}`} data-testid="message-btn">
              <Button variant="outline" className="btn-pill">
                <MessageCircle className="w-4 h-4 mr-1.5" /> {t("message")}
              </Button>
            </Link>
            <Button
              data-testid="compare-btn"
              variant="outline"
              className="btn-pill"
              onClick={() => setCompareOpen(true)}
            >
              <Scale className="w-4 h-4 mr-1.5" /> Compare
            </Button>
            {/* Family Chat button removed at user request */}
            <Button
              data-testid="ai-icebreaker-btn"
              onClick={async () => {
                setIcebreakerOpen(true);
                setIcebreakers(null);
                setIcebreakerLoading(true);
                try {
                  const { data } = await api.post("/ai/icebreakers", { other_user_id: p.id });
                  setIcebreakers(data.icebreakers || []);
                } catch (e) { toast.error(formatError(e)); }
                finally { setIcebreakerLoading(false); }
              }}
              variant="outline"
              className="btn-pill border-[#DDD6FE] text-[#7C3AED] hover:bg-[#F5F3FF]"
            >
              <Sparkles className="w-4 h-4 mr-1.5" /> Suggest opening
            </Button>
            {showEmojis && (
              <div data-testid="emoji-picker" className="absolute -bottom-14 left-24 border shadow-lg rounded-full px-3 py-2 flex gap-2 z-20" style={{ background: "var(--umg-surface)", borderColor: "var(--umg-border)" }}>
                {EMOJIS.map((e) => (
                  <button key={e} data-testid={`emoji-${e}`} onClick={() => doEmoji(e)} className="text-xl hover:scale-125 transition">
                    {e}
                  </button>
                ))}
              </div>
            )}
          </div>
          )}

          {/* View contact — premium/admin only */}
          {!isOwner && (user?.is_premium || user?.role === "admin") && !contact && (
            <div className="mt-6">
              <Button data-testid="view-contact-btn" variant="outline" onClick={viewContact} className="btn-pill">
                <PhoneIcon className="w-4 h-4 mr-1.5" /> View contact number
              </Button>
            </div>
          )}
          {!isOwner && !user?.is_premium && user?.role !== "admin" && (
            <div data-testid="contact-locked" className="mt-6 p-4 border flex items-center justify-between gap-4" style={{ borderRadius: 8, borderColor: "var(--umg-border)", background: "var(--umg-secondary)" }}>
              <div className="min-w-0">
                <div className="text-sm font-semibold flex items-center gap-1.5">
                  <Lock className="w-4 h-4 text-[#C85A53]" /> Contact details locked
                </div>
                <div className="text-xs text-[color:var(--umg-text-2)] mt-0.5">
                  Mobile and email are visible to Premium members only.
                </div>
              </div>
              <Link to="/premium"><Button size="sm" className="btn-pill bg-[#C85A53] hover:bg-[#A94842] text-white h-9">Upgrade</Button></Link>
            </div>
          )}
          {!isOwner && contact && (
            <div data-testid="contact-revealed" className="mt-6 p-4 border" style={{ borderRadius: 8, borderColor: "var(--umg-border)", background: "var(--umg-secondary)" }}>
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--umg-text-2)]">Contact</div>
              <div className="mt-1 text-sm">📞 {contact.mobile || "—"} · ✉️ {contact.email || "—"}</div>
              <div className="text-[10px] text-[color:var(--umg-text-2)] mt-1">{p.name} will be notified that you viewed their contact.</div>
            </div>
          )}

          {/* Data grid */}
          <div className="mt-10 grid sm:grid-cols-2 gap-x-8">
            <Field label={t("religion")} value={p.religion} />
            <Field label="Mother tongue" value={p.mother_tongue} />
            <Field label={t("diet")} value={p.diet} />
            <Field label={t("marital")} value={p.marital_status} />
            <Field label="Children" value={p.has_children ? `Yes (${p.children_count || ""})` : p.has_children === false ? "No" : null} />
            <Field label="Income" value={p.income} />
            <Field label="Family type" value={p.family_type} />
            <Field label="Family size" value={p.family_size} />
            <Field label="Caste" value={p.caste} />
            <Field label="Hobbies" value={(p.hobbies || []).join(", ")} />
          </div>

          {p.about && (
            <div className="mt-8">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--umg-text-2)]">{t("about")}</div>
              <p className="mt-2 leading-relaxed">{p.about}</p>
            </div>
          )}

          {/* AI Compatibility breakdown */}
          {p.compatibility && (
            <div className="mt-8" data-testid="compatibility-card">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--umg-text-2)] flex items-center gap-2">
                AI Compatibility
                <span className="inline-flex items-center gap-1 text-[9px] bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white px-1.5 py-0.5 rounded">Powered by Umaang AI</span>
              </div>
              <div className="mt-3 p-5 rounded-2xl border" style={{ borderColor: "#EDE9FE", background: "linear-gradient(135deg,#F5F3FF 0%,#FCE7F3 100%)" }}>
                <div className="flex items-center gap-5">
                  <div className="relative w-24 h-24 shrink-0">
                    <svg viewBox="0 0 100 100" className="w-24 h-24 -rotate-90">
                      <circle cx="50" cy="50" r="44" strokeWidth="10" stroke="#E9D5FF" fill="none" />
                      <circle
                        cx="50" cy="50" r="44"
                        strokeWidth="10"
                        strokeLinecap="round"
                        stroke="url(#compat-grad)"
                        fill="none"
                        strokeDasharray={`${(p.compatibility.overall / 100) * 276.46} 276.46`}
                      />
                      <defs>
                        <linearGradient id="compat-grad" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor="#7C3AED" />
                          <stop offset="100%" stopColor="#DB2777" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <div data-testid="compatibility-overall" className="font-display text-2xl leading-none" style={{ color: "#7C3AED" }}>{p.compatibility.overall}%</div>
                      <div className="text-[8px] uppercase tracking-widest text-[color:var(--umg-text-2)]">overall</div>
                    </div>
                  </div>
                  <div className="flex-1 space-y-2.5">
                    {[
                      { key: "traditional", label: "Traditional match", val: p.compatibility.traditional, color: "#C85A53" },
                      { key: "personality", label: "Personality & values", val: p.compatibility.personality, color: "#7C3AED" },
                      { key: "lifestyle",   label: "Lifestyle proximity", val: p.compatibility.lifestyle, color: "#DB2777" },
                    ].map(({ key, label, val, color }) => (
                      <div key={key} data-testid={`compatibility-bar-${key}`}>
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="text-[color:var(--umg-text-2)]">{label}</span>
                          <span className="font-semibold" style={{ color }}>{val}%</span>
                        </div>
                        <div className="mt-1 h-1.5 rounded-full overflow-hidden" style={{ background: "#EDE9FE" }}>
                          <div className="h-full transition-all duration-500" style={{ width: `${val}%`, background: color }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {!p.compatibility.both_completed && (
                  <div className="mt-4 pt-4 border-t border-[#E9D5FF] flex flex-wrap items-center justify-between gap-3">
                    <div className="text-xs text-[#5B21B6]">
                      💡 Personality score is <b>{p.compatibility.answered_questions === 0 ? "unavailable" : "partial"}</b> — take the 60-second quiz to unlock full AI matching.
                    </div>
                    <Link to="/quiz" data-testid="compatibility-quiz-cta" className="btn-pill bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-xs px-4 py-1.5 rounded-full">
                      Take quiz →
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Social & Professional links (Premium-visible) */}
          {(Object.keys(p.social_links || {}).length > 0 || p.social_links_locked) && (
            <div className="mt-8" data-testid="social-links-block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--umg-text-2)] flex items-center gap-2">
                Social &amp; Professional
                <span className="inline-flex items-center gap-1 text-[9px] bg-[#EEF2FF] text-[#4F46E5] px-1.5 py-0.5 rounded">Verified links</span>
              </div>
              {p.social_links_locked ? (
                <div className="mt-3 p-4 border-2 border-dashed rounded-xl flex flex-col sm:flex-row items-center justify-between gap-3" style={{ borderColor: "#F59E0B44", background: "#FEF3C7" }}>
                  <div className="flex items-center gap-3">
                    <Lock className="w-5 h-5 text-[#B45309]" />
                    <div>
                      <div className="text-sm font-semibold text-[#78350F]">{p.social_links_count} social {p.social_links_count === 1 ? "profile" : "profiles"} available</div>
                      <div className="text-xs text-[#92400E]">Upgrade to Premium to see LinkedIn &amp; more.</div>
                    </div>
                  </div>
                  <Link to="/premium" data-testid="social-locked-cta" className="btn-pill bg-[#C85A53] hover:bg-[#A94842] text-white text-sm px-4 py-2 rounded-full">Unlock →</Link>
                </div>
              ) : (
                <div className="mt-3 flex flex-wrap gap-2">
                  {Object.entries(p.social_links || {}).map(([k, url]) => {
                    const meta = SOCIAL_META[k];
                    if (!meta) return null;
                    const { Icon, label, color } = meta;
                    return (
                      <a
                        key={k}
                        data-testid={`social-chip-${k}`}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium hover:shadow-sm transition"
                        style={{ borderColor: `${color}44`, color, background: `${color}0d` }}
                        title={url}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {label}
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {horo && horo.ready && (
            <div className="mt-8 p-6 border" style={{ borderRadius: 8, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--umg-text-2)]">Guna Milan</div>
                  <div className="font-display text-2xl mt-1">{horo.score} / {horo.max}</div>
                </div>
                <div className={`px-3 py-1 rounded-full text-xs font-semibold ${horo.score >= 24 ? "bg-[#4F7942] text-white" : horo.score >= 18 ? "bg-[#C85A53] text-white" : "bg-[color:var(--umg-secondary)]"}`}>
                  {horo.score >= 24 ? "Excellent" : horo.score >= 18 ? "Good" : "Consider"}
                </div>
              </div>
              <div className="mt-4 grid grid-cols-4 gap-2 text-xs">
                {Object.entries(horo.aspects).map(([k, v]) => (
                  <div key={k} className="p-2 border" style={{ borderRadius: 4, borderColor: "var(--umg-border)" }}>
                    <div className="text-[9px] uppercase tracking-widest text-[color:var(--umg-text-2)]">{k}</div>
                    <div className="font-semibold mt-0.5">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sticky bottom Connect bar */}
      {!isOwner && (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 px-3 max-w-md w-full">
        <div className="rounded-full shadow-[0_20px_60px_rgba(28,25,23,0.15)] px-3 py-2 flex items-center justify-between gap-3 border" style={{ background: "var(--umg-surface)", borderColor: "var(--umg-border)" }}>
          <div className="min-w-0 flex-1 flex items-center gap-2 pl-2">
            <Handshake className="w-4 h-4 text-[#C85A53] shrink-0" />
            <span className="text-sm truncate">
              {sent?.status === "accepted" || received?.status === "accepted"
                ? `Connected with ${p.name}`
                : sent?.status === "pending"
                ? `Request sent to ${p.name}`
                : sent?.status === "rejected"
                ? `Request declined`
                : `Interested in ${p.name}?`}
            </span>
          </div>
          {sent?.status === "pending" ? (
            <Button disabled data-testid="connect-pending" size="sm" className="btn-pill h-9">Pending</Button>
          ) : sent?.status === "accepted" || received?.status === "accepted" ? (
            <Link to={`/messages/${p.id}`}><Button data-testid="connect-chat" size="sm" className="btn-pill bg-[#4F7942] hover:bg-[#3d5f34] text-white h-9">Chat</Button></Link>
          ) : (
            <Button data-testid="connect-btn" size="sm" onClick={() => setConnectOpen(true)} className="btn-pill bg-[#C85A53] hover:bg-[#A94842] text-white h-9">
              Connect
            </Button>
          )}
        </div>
      </div>
      )}

      {/* Connect dialog */}
      <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send connection request to {p.name}</DialogTitle>
            <DialogDescription>Add a short note (optional). Contact info will be masked.</DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="connect-msg"
            rows={3}
            value={connectMsg}
            onChange={(e) => setConnectMsg(e.target.value)}
            placeholder="Hi! I like your profile — would you be open to connect?"
          />
          <DialogFooter>
            <Button data-testid="connect-send" onClick={doConnect} className="btn-pill bg-[#C85A53] hover:bg-[#A94842] text-white">Send request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report {p.name}</DialogTitle>
            <DialogDescription>Our team reviews reports within 24 hours.</DialogDescription>
          </DialogHeader>
          <Input
            data-testid="report-reason"
            value={reportReason}
            onChange={(e) => setReportReason(e.target.value)}
            placeholder="Reason — fake photos, harassment, etc."
          />
          <DialogFooter>
            <Button data-testid="report-submit" onClick={submitReport} className="btn-pill bg-[#C85A53] hover:bg-[#A94842] text-white">Submit report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Icebreaker dialog */}
      <Dialog open={icebreakerOpen} onOpenChange={setIcebreakerOpen}>
        <DialogContent data-testid="icebreaker-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-2xl">
              <Sparkles className="w-5 h-5 text-[#7C3AED]" /> Opening line ideas
            </DialogTitle>
            <DialogDescription className="text-xs">
              AI-generated based on both profiles. Personalise before sending.
            </DialogDescription>
          </DialogHeader>
          {icebreakerLoading ? (
            <div className="py-10 text-center text-sm text-[color:var(--umg-text-2)]">
              <div className="inline-flex items-center gap-1 text-[#7C3AED]">
                <span className="animate-bounce">·</span>
                <span className="animate-bounce" style={{ animationDelay: "0.15s" }}>·</span>
                <span className="animate-bounce" style={{ animationDelay: "0.3s" }}>·</span>
              </div>
              <div className="mt-2">Umaang AI is thinking…</div>
            </div>
          ) : (
            <div className="space-y-2.5">
              {(icebreakers || []).map((line, i) => (
                <div key={`ice-${i}-${(line || "").slice(0, 24)}`} data-testid={`icebreaker-${i}`} className="p-3 rounded-xl border" style={{ borderColor: "#DDD6FE", background: "#F9FAFB" }}>
                  <p className="text-sm">{line}</p>
                  <div className="flex justify-end mt-2 gap-2">
                    <button
                      data-testid={`icebreaker-copy-${i}`}
                      onClick={() => { navigator.clipboard?.writeText(line); toast.success("Copied!"); }}
                      className="text-[10px] uppercase tracking-widest text-[#7C3AED] hover:underline"
                    >Copy</button>
                    <Link
                      to={`/messages/${p.id}`}
                      data-testid={`icebreaker-use-${i}`}
                      onClick={() => { navigator.clipboard?.writeText(line); toast.success("Copied — paste in chat"); setIcebreakerOpen(false); }}
                      className="text-[10px] uppercase tracking-widest bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white px-3 py-1 rounded-full"
                    >Use →</Link>
                  </div>
                </div>
              ))}
              {icebreakers && icebreakers.length === 0 && (
                <p className="text-sm text-[color:var(--umg-text-2)] py-4 text-center">Could not generate suggestions. Please try again.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Compare dialog — side-by-side me vs them */}
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Compare with your profile</DialogTitle>
            <DialogDescription>Side-by-side match kar ke dekh sakte ho — {me?.name || "You"} vs {p.name}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            {[
              { label: "Age", get: (x) => x?.age },
              { label: "Height", get: (x) => x?.height },
              { label: "Marital status", get: (x) => x?.marital_status },
              { label: "Religion", get: (x) => x?.religion },
              { label: "Caste / Community", get: (x) => x?.caste },
              { label: "Mother tongue", get: (x) => x?.mother_tongue },
              { label: "City", get: (x) => x?.city },
              { label: "State", get: (x) => x?.state },
              { label: "Education", get: (x) => x?.education },
              { label: "Profession", get: (x) => x?.profession },
              { label: "Employment", get: (x) => x?.employment_type },
              { label: "Annual income", get: (x) => x?.income },
              { label: "Diet", get: (x) => x?.diet },
              { label: "Family type", get: (x) => x?.family_type },
              { label: "Hobbies", get: (x) => (Array.isArray(x?.hobbies) ? x.hobbies.join(", ") : x?.hobbies) },
            ].map((row) => {
              const mine = row.get(me);
              const theirs = row.get(p);
              const both = mine && theirs;
              const match = both && String(mine).toLowerCase() === String(theirs).toLowerCase();
              return (
                <React.Fragment key={row.label}>
                  <div className="col-span-2 mt-2 text-[10px] uppercase tracking-widest text-[color:var(--umg-text-2)] font-semibold border-b pb-1" style={{ borderColor: "var(--umg-border)" }}>{row.label}{match && <span className="ml-2 text-emerald-600">· match ✓</span>}</div>
                  <div className={`text-sm px-3 py-2 rounded ${match ? "bg-emerald-50 border border-emerald-200" : "bg-[color:var(--umg-secondary)]"}`}>
                    <div className="text-[10px] uppercase tracking-widest text-[color:var(--umg-text-2)] mb-0.5">You</div>
                    <div className="font-medium">{mine || <span className="text-[color:var(--umg-text-2)]">—</span>}</div>
                  </div>
                  <div className={`text-sm px-3 py-2 rounded ${match ? "bg-emerald-50 border border-emerald-200" : "bg-[color:var(--umg-secondary)]"}`}>
                    <div className="text-[10px] uppercase tracking-widest text-[color:var(--umg-text-2)] mb-0.5">{p.name}</div>
                    <div className="font-medium">{theirs || <span className="text-[color:var(--umg-text-2)]">—</span>}</div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
